import React, { useState, useMemo } from 'react';
import { useTranslation } from 'utils/i18n';
import { useDarkMode } from 'utils/darkMode';
import sharedTranslations from '../shared/translations';
import { colors, typography, spacing, interactive } from './legalStyles';

const indent = (level) => ({ paddingLeft: `${(level - 1) * 12}px` });

const TocItem = ({ node, level = 1, activeId, language, isDarkMode = false }) => {
  const [open, setOpen] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isActive = activeId === node.id;
  
  // Get translations - memoized to update when language changes
  const translations = useMemo(() => {
    const t = sharedTranslations[language] || sharedTranslations.en;
    return {
      collapseAriaLabel: t?.legal?.toc?.collapseAriaLabel || 'Collapse section',
      expandAriaLabel: t?.legal?.toc?.expandAriaLabel || 'Expand section'
    };
  }, [language]);

  const itemStyle = {
    marginBottom: spacing.xs
  };

  const linkContainerStyle = {
    ...indent(level),
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: '4px',
    transition: 'background-color 0.2s ease'
  };

  const linkStyle = {
    ...interactive.link,
    textDecoration: 'none',
    color: isActive 
      ? (isDarkMode ? '#60a5fa' : colors.primary)
      : (isDarkMode ? '#cbd5e0' : colors.text),
    fontWeight: isActive ? '600' : '400',
    flex: 1
  };

  const iconStyle = {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: isDarkMode ? '#94a3b8' : colors.textSecondary,
    padding: 0,
    width: '16px',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <div className={`legal-toc-item level-${level}`} style={itemStyle}>
      <div
        className={`legal-toc-link ${isActive ? 'active' : ''}`}
        style={linkContainerStyle}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = isDarkMode ? '#2d3748' : colors.backgroundHover;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {hasChildren ? (
          <button
            aria-label={open ? translations.collapseAriaLabel : translations.expandAriaLabel}
            onClick={() => setOpen(!open)}
            style={iconStyle}
            onMouseEnter={(e) => {
              e.target.style.color = isDarkMode ? '#e2e8f0' : colors.text;
            }}
            onMouseLeave={(e) => {
              e.target.style.color = isDarkMode ? '#94a3b8' : colors.textSecondary;
            }}
          >
            {open ? '▾' : '▸'}
          </button>
        ) : (
          <span style={{ width: '16px' }} />
        )}
        <a 
          href={`#${node.id}`} 
          style={linkStyle}
          onClick={(e) => {
            e.preventDefault();
            const element = document.getElementById(node.id);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              // Update URL without scrolling
              window.history.pushState(null, '', `#${node.id}`);
            }
          }}
        >
          {node.title}
        </a>
      </div>
      {hasChildren && open ? (
        <div>
          {node.children.map((child) => (
            <TocItem key={child.id} node={child} level={level + 1} activeId={activeId} language={language} isDarkMode={isDarkMode} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const flatten = (sections) => sections.map(s => ({
  id: s.id,
  title: s.title,
  children: s.children ? flatten(s.children) : []
}));

const LegalToc = ({ sections = [], activeId }) => {
  const { language } = useTranslation();
  const { isDarkMode } = useDarkMode();
  const tree = useMemo(() => flatten(sections), [sections]);
  
  // Get translations - memoized to update when language changes
  const translations = useMemo(() => {
    const t = sharedTranslations[language] || sharedTranslations.en;
    return {
      ariaLabel: t?.legal?.toc?.ariaLabel || 'Table of contents'
    };
  }, [language]);
  
  const navStyle = {
    marginTop: spacing.md
  };

  return (
    <nav className="legal-toc" aria-label={translations.ariaLabel} style={navStyle}>
      {tree.map(node => (
        <TocItem key={node.id} node={node} level={1} activeId={activeId} language={language} isDarkMode={isDarkMode} />
      ))}
    </nav>
  );
};

export default LegalToc;
